import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ViewChild,
  ChangeDetectorRef,
  OnInit,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { take, filter } from 'rxjs/operators';
import _pick from 'lodash-es/pick';
import _size from 'lodash-es/size';
import _map from 'lodash-es/map';
import { DynamicFormField, DynamicFormSection } from 'interfaces/dynamic-form-field.interface';
import { DynamicFormComponent } from 'components/dynamic-form/dynamic-form.component';
import { PAGES } from '../../dynamic-details.constants';
import { constants } from 'constants/constants';
import { DynamicFormContentDetails } from '../dynamic-form-content-details/dynamic-form-content-details.component';
import {
  DynamicFormValue,
  DynamicFormValueData,
} from 'components/multiple-dynamic-form/multiple-dynamic-form.interface';
import { SmartModalService } from '@certemy/ui-components';
import { ComputedFrom } from '@certemy/common';
import _cloneDeep from 'lodash-es/cloneDeep';

@Component({
  selector: 'cm-dynamic-form-content-sequence',
  templateUrl: './dynamic-form-content-sequence.component.html',
  styleUrls: ['./dynamic-form-content-sequence.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DynamicFormContentSequenceComponent implements OnInit {
  _elements: DynamicFormField[] = [];
  @ViewChild(DynamicFormComponent) dynamicFormComponent: DynamicFormComponent;
  @Input() modalDetails: DynamicFormContentDetails;
  @Input() formData: DynamicFormValueData;
  @Input() set elements(data: DynamicFormField[]) {
    const copy = _cloneDeep(data);
    copy.forEach((elem, i, arr) => {
      if (_size(elem.dependencyData)) {
        elem.dependencyData.forEach((depData) => {
          const field = arr.find(({ id }) => id === depData.fieldId);
          if (field) {
            field.isWatchChangeValue = true;
          }
        });
      }
    });
    this._elements = copy;
  }
  @Input() sections: DynamicFormSection[];
  @Input() showFromStart = true;
  @Input() isFirstPage = false;
  @Input() disabled = false;

  @Output() screenChange = new EventEmitter<string>();
  @Output() formSubmit = new EventEmitter<DynamicFormValue>();
  @Output() formDismiss = new EventEmitter();

  dynamicForm: UntypedFormGroup;
  elementsArray: DynamicFormField[][];
  showErrors = false;
  dynamicFormErrors: string[] = [];
  activeSequenceIndex = 0;
  FIELD_TYPES = constants.FORM_FIELD_TYPE_IDS;
  SELECT_SUB_TYPES = constants.SELECT_SUB_TYPES;
  private tempFormData = {};

  constructor(private changeDetector: ChangeDetectorRef, private smartModalService: SmartModalService) {}

  ngOnInit() {
    this.initFormData();
    this.setDependencyData();
    const index = this.showFromStart ? 0 : this.lastSequenceIndex;

    this.elementsArray = _map(this._elements, ({ dependencyData, ...elem }) => {
      const parentField = this._elements.find((item) => dependencyData && item.id === dependencyData[0].fieldId);
      if (elem.subType === this.SELECT_SUB_TYPES.STATE && elem.type.id === this.FIELD_TYPES.SELECT && parentField) {
        return [
          {
            ...elem,
            hidden: false,
            dependencyData,
          },
          {
            ...parentField,
            hidden: true,
            value: null,
            _isSequence: true,
          },
        ];
      }
      return [{ ...elem }];
    });
    this.updateActiveIndex(index);
    this.updateFormValue(index);
  }

  @ComputedFrom('_elements', 'formData', 'tempFormData')
  get lastSequenceIndex(): number {
    return this._elements.reduce((res, el, i) => {
      return !this.isHidden(el) ? i : res;
    }, 0);
  }

  private updateActiveIndex(index: number) {
    this.activeSequenceIndex = index;
    this.changeDetector.detectChanges();
  }

  private updateFormValue(index: number) {
    const data = _pick(this.formData, _map(this.elementsArray[index], 'id'));

    if (data && this.dynamicFormComponent) {
      this.dynamicFormComponent.dynamicForm.patchValue(data);
      this.changeDetector.detectChanges();
    }
  }

  removeValues(index: number) {
    const data = _pick(this.formData, _map(this.elementsArray[index], 'id'));
    Object.keys(data).forEach((id) => {
      delete this.formData[id];
    });
  }

  private upateFormValidation(isValid: boolean) {
    this.showErrors = !isValid;
    this.dynamicFormErrors = this.dynamicFormComponent.getFormErrorMessages();
  }

  private resetFormValidation() {
    this.showErrors = false;
    this.dynamicFormErrors = [];
  }

  showUnsavedChangesModal() {
    this.smartModalService
      .openUnsavedChanges()
      .pipe(
        take(1),
        filter((canDeactivate) => canDeactivate),
      )
      .subscribe(() => this.formDismiss.emit());
  }

  onBack() {
    if (this.activeSequenceIndex) {
      const form = this.dynamicFormComponent.dynamicForm;
      this.formData = { ...this.formData, ...form.value };
      this.resetFormValidation();
      let nextIndex = this.activeSequenceIndex;
      do {
        nextIndex--;
      } while (this.isHidden(this._elements[nextIndex]));
      this.updateActiveIndex(nextIndex);
      this.updateFormValue(nextIndex);

      return;
    }

    this.screenChange.emit(PAGES.BACK);
  }

  onNext() {
    if (!this.dynamicFormComponent) {
      return;
    }

    const form = this.dynamicFormComponent.dynamicForm;
    const { valid, value } = form;
    this.upateFormValidation(valid);

    if (!valid) {
      return;
    }

    this.formData = { ...this.formData, ...value };
    this.setDependencyData();

    if (this.lastSequenceIndex === this.activeSequenceIndex) {
      if (this.lastSequenceIndex !== this._elements.length - 1) {
        let index = this.lastSequenceIndex;
        while (index !== this._elements.length - 1) {
          this.removeValues(++index);
        }
      }
      this.completeForm();
    } else {
      this.showNextForm();
    }
  }

  completeForm() {
    const formData: DynamicFormValue = { formValue: this.formData };
    this.formSubmit.emit(formData);
  }

  showNextForm() {
    let nextIndex = this.activeSequenceIndex;
    let isHidden;
    do {
      nextIndex++;
      isHidden = this.isHidden(this._elements[nextIndex]);
      if (isHidden) {
        this.removeValues(nextIndex);
      }
    } while (isHidden);
    this.updateActiveIndex(nextIndex);
    this.updateFormValue(nextIndex);
  }

  private setValueByType(element) {
    const defaultValue = element.type.id === this.FIELD_TYPES.ATTACHMENT ? [] : '';
    return element.value || defaultValue;
  }

  private initFormData() {
    this.formData =
      this.formData ||
      this._elements.reduce((acc, elem) => {
        const isHidden = this.isHidden(elem);
        if (isHidden) {
          return acc;
        }
        return { ...acc, [elem.id]: this.setValueByType(elem) };
      }, {});
    this.tempFormData = _cloneDeep(this.formData);
  }

  private isHidden(element) {
    const arr = this._elements;
    return (
      _size(element.dependencyData) &&
      !element.dependencyData.every((data) => {
        const { fieldId, optionId } = data;
        const getValueFromFormData =
          (this.tempFormData && this.tempFormData.hasOwnProperty(fieldId)) ||
          (this.formData && this.formData.hasOwnProperty(fieldId));
        const field = _size(arr) ? arr.find((_f) => _f.id === fieldId) : null;
        const value = getValueFromFormData
          ? this.tempFormData[fieldId] || this.formData[fieldId]
          : field && field.value;
        return value === optionId;
      })
    );
  }

  updateFieldValue({ field: { id, value } }) {
    this.tempFormData = {
      ...this.tempFormData,
      [id]: value,
    };
  }

  private setDependencyData() {
    this._elements = this._elements.map((item) => {
      if (
        item.subType === this.SELECT_SUB_TYPES.STATE &&
        item.type.id === this.FIELD_TYPES.SELECT &&
        item.dependencyData &&
        this.formData[item.dependencyData[0].fieldId]
      ) {
        this.updateFieldValue({
          field: { id: item.dependencyData[0].fieldId, value: this.formData[item.dependencyData[0].fieldId] },
        });
        return {
          ...item,
          dependencyData: [{ ...item.dependencyData[0], optionId: this.formData[item.dependencyData[0].fieldId] }],
        };
      }

      return item;
    });
  }
}
